import React from 'react';
import dayjs from 'op-utils/dayjs';
import { ChatHistoryItem, ChatRequest } from '../Interface';
import { CitationProps } from '../CitationPanel';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { AssignmentInd as AssignmentIndIcon, Notes as NotesIcon, PictureAsPdf as PDFIcon } from '@mui/icons-material';

interface CitationSnippets {
  content: string;
  page: string;
}

interface SingleCitationProps {
  filename: string;
  filepath: string;
  extension: string;
  documentDate?: string;
  snippets?: CitationSnippets[];
}

/**
 * Determine the icon for a citation based on file extension and filename
 */
export const getCitationIcon = (citation: SingleCitationProps): React.ReactElement => {
  if (!citation.extension) return <InsertDriveFileIcon />;

  if (citation.filename?.toLowerCase().includes('health assessment')) {
    return <AssignmentIndIcon />;
  }

  if (citation.extension === 'pdf') {
    return <PDFIcon />;
  }

  return <NotesIcon />;
};

/**
 * Convert GraphQL citations to CitationProps format
 */
export const convertGraphQLCitationsToCitationProps = (
  citations: any[],
  conversationId: number,
  requestIndex: number,
): CitationProps[] => {
  return citations.map((citation: any, citationIndex: number): CitationProps => {
    const extension = citation.extension?.toLowerCase() || 'pdf';
    const backendDocType = citation.documentType;
    const filepath = citation.filepath || '';
    const resourceId = citation.resourceId || '';

    let documentType: string;
    if (backendDocType) {
      documentType = backendDocType;
    } else {
      documentType = extension === 'txt' ? 'text' : 'pdf';
    }

    // For DocumentPart/Note citations, use resourceId as documentUrl so the
    // frontend can call getSummaryDocument for a fresh presigned URL.
    // Stored presigned URLs expire, so we prefer the stable resourceId.
    let documentUrl = filepath;
    if ((documentType === 'DocumentPart' || documentType === 'Note') && resourceId) {
      documentUrl = resourceId;
    }

    return {
      title: citation.filename || 'Document',
      documentUrl,
      documentType,
      dateOfDocument: citation.documentDate || '',
      status: citation.status || '',
      author: citation.authorName || undefined,
      icon: getCitationIcon(citation),
      id: `${conversationId}-${requestIndex}-${citationIndex}`,
      snippets:
        citation.snippets?.map((snippet: any) => ({
          page: snippet.page || '',
          text: snippet.content || '',
        })) || [],
    };
  });
};

/**
 * Parse citations from response_data (fallback for older messages)
 */
export const parseCitationsFromResponseData = (
  responseData: any,
  conversationId: number,
  requestIndex: number,
): CitationProps[] => {
  if (!responseData?.citations || !Array.isArray(responseData.citations)) {
    return [];
  }

  // Use filepath as key to avoid collision when different documents normalize to the same filename
  const citationsMap: { [key: string]: any } = {};

  responseData.citations.forEach((citation: any) => {
    citation.retrievedReferences?.forEach((ref: any) => {
      const filepath = ref.location?.s3Location?.uri;
      if (!filepath) return;

      const extension = filepath.split('.').pop() || 'pdf';
      const rawFilename = filepath.split('/').pop()?.split('.')[0] || 'Document';
      const filename = rawFilename.replace(/-redacted|-deidentified/gi, '').replace(/redacted/gi, '');
      const pageNumber = ref.metadata?.['x-amz-bedrock-kb-document-page-number'];
      const content = ref.content?.text;

      if (!citationsMap[filepath]) {
        citationsMap[filepath] = {
          filename,
          filepath,
          extension,
          pageNumber: pageNumber ? parseInt(pageNumber, 10) : null,
          documentDate: '',
          snippets: [],
        };
      }

      if (content) {
        citationsMap[filepath].snippets.push({
          content,
          page: pageNumber ? String(pageNumber) : '',
        });
      }
    });
  });

  return Object.values(citationsMap).map(
    (citation: any, citationIndex: number): CitationProps => ({
      title: citation.filename || 'Document',
      documentUrl: citation.filepath || '',
      documentType: citation.extension?.toLowerCase() === 'txt' ? 'text' : 'pdf',
      dateOfDocument: citation.documentDate || '',
      status: citation.status || '',
      // author not known for legacy response_data; leave undefined
      icon: getCitationIcon(citation),
      id: `${conversationId}-${requestIndex}-${citationIndex}`,
      snippets:
        citation.snippets?.map((snippet: any) => ({
          page: snippet.page || '',
          text: snippet.content || '',
        })) || [],
    }),
  );
};

/**
 * Group messages into request/response pairs
 */
export const groupMessagesIntoRequests = (messages: any[], conversationId: number): ChatRequest[] => {
  const requests: ChatRequest[] = [];
  let currentRequest: Partial<ChatRequest> | null = null;

  messages.forEach((message: any) => {
    if (message.role === 'user') {
      if (currentRequest?.response?.trim()) {
        requests.push(currentRequest as ChatRequest);
      }

      currentRequest = {
        text: message.content,
        createdAt: message.createdAt,
        type: '',
        response: '',
        citations: [],
      };
    } else if (message.role === 'assistant' && currentRequest) {
      currentRequest.response = message.content;
      if (message.createdAt && !currentRequest.responseCreatedAt) {
        currentRequest.responseCreatedAt = message.createdAt;
      }

      // Parse responseData if it's a JSON string (GraphQL JSONString returns a string)
      let parsedResponseData = message.responseData;
      if (typeof message.responseData === 'string') {
        try {
          parsedResponseData = JSON.parse(message.responseData);
        } catch {
          parsedResponseData = null;
        }
      }

      // Extract type from responseData if available (for special template types)
      if (parsedResponseData?.type) {
        currentRequest.type = parsedResponseData.type;
      }

      // Extract letter_html from responseData for document types (hpi_document, etc.)
      if (parsedResponseData?.letter_html) {
        currentRequest.letterHtml = parsedResponseData.letter_html;
      }

      // Extract is_edit flag from responseData (indicates if this is an edit vs generation)
      if (parsedResponseData?.is_edit !== undefined) {
        currentRequest.isEdit = parsedResponseData.is_edit;
      }

      // Process citations - prefer GraphQL citations, fallback to response_data
      if (message.citations && Array.isArray(message.citations) && message.citations.length > 0) {
        currentRequest.citations = convertGraphQLCitationsToCitationProps(
          message.citations,
          conversationId,
          requests.length,
        );
      } else if (parsedResponseData?.citations) {
        currentRequest.citations = parseCitationsFromResponseData(parsedResponseData, conversationId, requests.length);
      } else {
        currentRequest.citations = [];
      }
    }
  });

  if (currentRequest) {
    // Only add the request if it has a response (completed)
    // Incomplete requests (user message without assistant response) indicate interrupted streams
    // These should be filtered out to prevent showing "..." indefinitely
    const response = (currentRequest as Partial<ChatRequest>).response;
    if (response && response !== '') {
      requests.push(currentRequest as ChatRequest);
    }
  }

  return requests;
};

/**
 * Group conversations by date (Today vs Previous Conversations)
 */
export const groupConversationsByDate = (
  conversations: any[],
  timezone: string,
  existingConversationsMap: Map<number, ChatHistoryItem>,
): ChatHistoryItem[] => {
  const today = dayjs().tz(timezone).startOf('day');
  const loadedConversations: ChatHistoryItem[] = [];
  let todaySeparatorAdded = false;
  let previousSeparatorAdded = false;

  conversations.forEach((conv: any) => {
    // Use updatedAt to determine "Today" vs "Previous" - so active conversations move to Today
    const convDate = dayjs
      .utc(conv.updatedAt || conv.createdAt)
      .tz(timezone)
      .startOf('day');
    const isToday = convDate.isSame(today, 'day');

    if (isToday && !todaySeparatorAdded) {
      loadedConversations.push({
        title: 'Today',
        requests: [],
        kind: 'separator',
      });
      todaySeparatorAdded = true;
    }

    if (!isToday && !previousSeparatorAdded) {
      loadedConversations.push({
        title: 'Previous Conversations',
        requests: [],
        kind: 'separator',
      });
      previousSeparatorAdded = true;
    }

    const existingConversation = existingConversationsMap.get(conv.id);
    // Clean up incomplete requests (empty response) from existing conversations
    // These indicate interrupted streams and should be removed
    const cleanedRequests = existingConversation?.requests?.filter((req) => req.response && req.response !== '') || [];
    loadedConversations.push({
      title: conv.title,
      conversationId: conv.id,
      requests: cleanedRequests,
      kind: 'conversation',
    });
  });

  return loadedConversations;
};

/**
 * Generate template citation URLs
 */
export const generateProstateMRIReferralTemplateCitation = (patientId: string) => {
  return {
    title: 'Imaging request - Draft',
    documentUrl: `${window.location.origin}/server/demo/aitemplate/${patientId}/prostate_mri_referral_template`,
    documentType: 'pdf',
  };
};

export const generateInitialConsultLetterTemplateCitation = (patientId: string) => {
  return {
    title: 'Initial Consultation Letter - Draft',
    documentUrl: `${window.location.origin}/server/demo/aitemplate/${patientId}/initial_consultation_letter_template`,
    documentType: 'letter',
  };
};
